/**
 * The in-page tab row — **SS1's tabs, inside one sidebar section**
 * (`DESIGN-SYSTEM.md` decision 3: seven sections do not fit one tab row, so the
 * sidebar owns the sections and the tab row lives inside each).
 *
 * Extracted because Stories, Spotlights and Channel Overview had three
 * byte-identical copies of this markup, and each copy carried its own
 * `aria-label` and `aria-current` wiring — three places for one of them to
 * silently lose its accessible name.
 */
import type { ReactNode } from 'react'

import { Hint } from './Hint'

export interface Tab<K extends string> {
  key: K
  label: ReactNode
  /** Announced to a screen reader in place of the visible label, where the
   *  label is a bare word like "English" that only makes sense in its row. */
  ariaLabel?: string
  /** `Segmented` only: the option cannot be chosen right now, and this says
   *  why on hover. Shown, never hidden, so the control does not change shape. */
  disabledReason?: string
}

export function Tabs<K extends string>({
  tabs,
  active,
  onChange,
  label,
}: {
  tabs: readonly Tab<K>[]
  active: K
  onChange: (key: K) => void
  /** What this row selects between — "Story type", "Window", "Language group". */
  label: string
}) {
  return (
    <nav
      className="mb-4 flex flex-wrap gap-1"
      style={{ borderBottom: '1px solid var(--border)' }}
      aria-label={label}
    >
      {tabs.map(({ key, label: text, ariaLabel }) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          aria-current={active === key}
          aria-label={ariaLabel}
          className="px-3 py-2 text-sm font-medium"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: active === key ? 'var(--brand-ink)' : 'var(--text-secondary)',
            borderBottom: active === key ? '2px solid var(--brand-ink)' : '2px solid transparent',
            marginBottom: '-1px',
          }}
        >
          {text}
        </button>
      ))}
    </nav>
  )
}

/**
 * A segmented control — the same selection semantics as `Tabs`, in a pill row
 * rather than an underlined row.
 *
 * Why both exist: Stories carries **three** selectors (content type, language
 * group, window), and three stacked underlined tab rows read as three levels of
 * hierarchy when only the first is one. The content tabs stay tabs; the two
 * that qualify the view become pills on one line.
 */
export function Segmented<K extends string>({
  options,
  active,
  onChange,
  label,
}: {
  options: readonly Tab<K>[]
  active: K
  onChange: (key: K) => void
  label: string
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs" style={{ color: 'var(--text-label)' }}>
        {label}
      </span>
      <div
        className="flex"
        role="group"
        aria-label={label}
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          overflow: 'hidden',
        }}
      >
        {options.map(({ key, label: text, disabledReason }, index) => {
          const selected = active === key
          const disabled = disabledReason !== undefined
          const button = (
            <button
              key={key}
              type="button"
              // aria-disabled, not disabled: a disabled button fires no mouse
              // events, so the reason could never be shown on hover.
              onClick={disabled ? undefined : () => onChange(key)}
              aria-pressed={selected}
              aria-disabled={disabled || undefined}
              className="px-3 py-1 text-xs"
              style={{
                background: selected ? 'var(--brand-tint)' : 'var(--bg-surface)',
                // --mint-700 territory: the brand mint is a surface colour at
                // 1.65:1 on white and must never carry text (DESIGN-SYSTEM.md).
                color: disabled
                  ? 'var(--text-label)'
                  : selected
                    ? 'var(--brand-ink)'
                    : 'var(--text-secondary)',
                fontWeight: selected ? 600 : 400,
                border: 'none',
                borderLeft: index === 0 ? 'none' : '1px solid var(--border)',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {text}
            </button>
          )
          return disabled ? (
            <Hint key={key} label={disabledReason} tabIndex={-1} className="flex">
              {button}
            </Hint>
          ) : (
            button
          )
        })}
      </div>
    </div>
  )
}
